import React, { useEffect, useState } from "react";
import axios from "axios";

const AdminUserManagement = () => {
    const [users, setUsers] = useState([]);
    const [roleFilter, setRoleFilter] = useState("all");
    const [sortOption, setSortOption] = useState("newest");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [editingUserId, setEditingUserId] = useState(null);
    const [editingUserData, setEditingUserData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [showNewUserForm, setShowNewUserForm] = useState(false);
    const [newUser, setNewUser] = useState({
        username: "",
        email: "",
        password: "",
        roles: ["user"],
    });

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(
                `/api/users?page=${currentPage}&role=${roleFilter}&sort=${sortOption}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setUsers(response.data.users);
            setTotalPages(response.data.totalPages);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to fetch users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [roleFilter, sortOption, currentPage]);

    const handleDelete = async (userId) => {
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`/api/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers(users.filter((user) => user._id !== userId));
        } catch (err) {
            setError(err.response?.data?.error || "Failed to delete user");
        }
    };

    const handleSave = async (userId) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.put(`/api/users/${userId}`, editingUserData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers(users.map((user) => (user._id === userId ? response.data : user)));
            setEditingUserId(null);
            setEditingUserData(null);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to save user changes");
        }
    };

    const handleRoleChange = async (userId, roles) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.put(
                `/api/users/${userId}/roles`,
                { roles },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setUsers((prevUsers) =>
                prevUsers.map((user) =>
                    user._id === response.data._id ? response.data : user
                )
            );
        } catch (err) {
            console.error("Error updating user roles:", err.response?.data || err.message);
            alert("Failed to update user roles");
        }
    };

    const handleAddUser = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post("/api/users", newUser, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers((prev) => [...prev, response.data]);
            setNewUser({ username: "", email: "", password: "", roles: ["user"] });
            setShowNewUserForm(false);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to add user");
        }
    };

    const exportToCSV = () => {
        const csvRows = [];
        const headers = ["Username", "Email", "Role", "Active", "Created", "Name", "Title", "Phone"];
        csvRows.push(headers.join(","));
        users.forEach((user) => {
            const row = [
                user.username,
                user.email,
                user.roles.join(", "),
                user.active ? "Yes" : "No",
                new Date(user.created).toLocaleString(),
                user.metadata?.name || "",
                user.metadata?.title || "",
                user.metadata?.phone || ""
            ];
            csvRows.push(row.join(","));
        });
        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "users.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleEdit = (user) => {
        if (editingUserId === user._id) {
            setEditingUserId(null);
            setEditingUserData(null);
        } else {
            setEditingUserId(user._id);
            setEditingUserData({ ...user });
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p className="text-red-500">{error}</p>;

    return (
        <div className="admin-user-management px-4">
            <h1 className="text-2xl font-bold mb-4">User Management</h1>

            {/* Filters and Sorting */}
            <div className="filters mb-4 flex flex-wrap gap-4">
                <div>
                    <label htmlFor="roleFilter" className="block font-semibold mb-2">
                        Filter by Role
                    </label>
                    <select
                        id="roleFilter"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="border rounded px-4 py-2"
                    >
                        <option value="all">All Roles</option>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>

                <div>
                    <label htmlFor="sortOption" className="block font-semibold mb-2">
                        Sort By
                    </label>
                    <select
                        id="sortOption"
                        value={sortOption}
                        onChange={(e) => setSortOption(e.target.value)}
                        className="border rounded px-4 py-2"
                    >
                        <option value="newest">Date Added (Newest)</option>
                        <option value="oldest">Date Added (Oldest)</option>
                        <option value="alphabetical">Alphabetical</option>
                    </select>
                </div>
            </div>

            {/* New User Form */}
            {showNewUserForm && (
                <div className="new-user-form border rounded p-4 mb-4">
                    <h3 className="text-lg font-semibold mb-4">Add New User</h3>
                    <input
                        type="text"
                        placeholder="Username"
                        value={newUser.username}
                        onChange={(e) =>
                            setNewUser({ ...newUser, username: e.target.value })
                        }
                        className="border rounded px-4 py-2 w-full mb-2"
                    />
                    <input
                        type="email"
                        placeholder="Email"
                        value={newUser.email}
                        onChange={(e) =>
                            setNewUser({ ...newUser, email: e.target.value })
                        }
                        className="border rounded px-4 py-2 w-full mb-2"
                    />
                    <input
                        type="password"
                        placeholder="Password"
                        value={newUser.password}
                        onChange={(e) =>
                            setNewUser({ ...newUser, password: e.target.value })
                        }
                        className="border rounded px-4 py-2 w-full mb-2"
                    />
                    <select
                        value={newUser.roles[0]}
                        onChange={(e) =>
                            setNewUser({ ...newUser, roles: [e.target.value] })
                        }
                        className="border rounded px-4 py-2 w-full mb-2"
                    >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                    </select>
                    <button
                        onClick={handleAddUser}
                        className="bg-green-500 text-white px-4 py-2 rounded"
                    >
                        Add User
                    </button>
                </div>
            )}
            <button
                onClick={() => setShowNewUserForm((prev) => !prev)}
                className="bg-blue-500 text-white px-4 py-2 rounded mb-4"
            >
                {showNewUserForm ? "Cancel" : "Add New User"}
            </button>
            <button
                onClick={exportToCSV}
                className="bg-yellow-500 text-white px-4 py-2 rounded ml-4"
            >
                Export Users to CSV
            </button>

            {/* User List */}
            <div>
                {users.length > 0 ? (
                    users.map((user) => (
                        <div
                            key={user._id}
                            className="border bg-white rounded-lg shadow p-4 mb-4"
                        >
                            {editingUserId === user._id ? (
                                <>
                                    <label>
                                        <strong>Username:</strong>
                                        <input
                                            type="text"
                                            value={editingUserData.username}
                                            onChange={(e) =>
                                                setEditingUserData({
                                                    ...editingUserData,
                                                    username: e.target.value,
                                                })
                                            }
                                            className="border rounded px-2 py-1 ml-2"
                                        />
                                    </label>
                                    <label>
                                        <strong>Email:</strong>
                                        <input
                                            type="text"
                                            value={editingUserData.email}
                                            onChange={(e) =>
                                                setEditingUserData({
                                                    ...editingUserData,
                                                    email: e.target.value,
                                                })
                                            }
                                            className="border rounded px-2 py-1 ml-2"
                                        />
                                    </label>
                                    <label>
                                        <strong>Name:</strong>
                                        <input
                                            type="text"
                                            value={editingUserData.metadata.name}
                                            onChange={(e) =>
                                                setEditingUserData({
                                                    ...editingUserData,
                                                    metadata: {
                                                        ...editingUserData.metadata,
                                                        name: e.target.value,
                                                    },
                                                })
                                            }
                                            className="border rounded px-2 py-1 ml-2"
                                        />
                                    </label>
                                    <label>
                                        <strong>Title:</strong>
                                        <input
                                            type="text"
                                            value={editingUserData.metadata.title}
                                            onChange={(e) =>
                                                setEditingUserData({
                                                    ...editingUserData,
                                                    metadata: {
                                                        ...editingUserData.metadata,
                                                        title: e.target.value,
                                                    },
                                                })
                                            }
                                            className="border rounded px-2 py-1 ml-2"
                                        />
                                    </label>
                                    <label>
                                        <strong>Phone Number:</strong>
                                        <input
                                            type="text"
                                            value={editingUserData.metadata.phone}
                                            onChange={(e) =>
                                                setEditingUserData({
                                                    ...editingUserData,
                                                    metadata: {
                                                        ...editingUserData.metadata,
                                                        phone: e.target.value,
                                                    },
                                                })
                                            }
                                            className="border rounded px-2 py-1 ml-2"
                                        />
                                    </label>
                                </>
                            ) : (
                                <>
                                    <p>
                                        <strong>Username:</strong> {user.username}
                                    </p>
                                    <p>
                                        <strong>Email:</strong> {user.email}
                                    </p>
                                    <p>
                                        <strong>Name:</strong> {user.metadata.name}
                                    </p>
                                    <p>
                                        <strong>Title:</strong> {user.metadata.title}
                                    </p>
                                    <p>
                                        <strong>Phone Number</strong> {user.metadata.phone}
                                    </p>
                                </>
                            )}
                            <p>
                                <strong>Role:</strong>
                                <select
                                    value={user.roles[0] || ""}
                                    onChange={(e) =>
                                        handleRoleChange(user._id, [e.target.value])
                                    }
                                    className="border px-2 py-1 rounded ml-2"
                                >
                                    <option value="user">User</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </p>
                            <p>
                                <strong>Active:</strong> {user.active ? "Yes" : "No"}
                            </p>
                            <p>
                                <strong>Created:</strong>{" "}
                                {new Date(user.created).toLocaleString()}
                            </p>
                            <div className="mt-2">
                                {editingUserId === user._id ? (
                                    <button
                                        className="bg-green-500 text-white px-2 py-1 rounded mr-2"
                                        onClick={() => handleSave(user._id)}
                                    >
                                        Save
                                    </button>
                                ) : null}
                                <button
                                    className={`${
                                        editingUserId === user._id
                                            ? "bg-gray-500"
                                            : "bg-blue-500"
                                    } text-white px-2 py-1 rounded mr-2`}
                                    onClick={() => toggleEdit(user)}
                                >
                                    {editingUserId === user._id ? "Cancel" : "Edit"}
                                </button>
                                <button
                                    className="bg-red-500 text-white px-2 py-1 rounded mr-2"
                                    onClick={() => handleDelete(user._id)}
                                >
                                    Delete
                                </button>
                                <button
                                    className="bg-yellow-500 text-white px-2 py-1 rounded"
                                    onClick={() =>
                                        handleSave(user._id, { active: !user.active })
                                    }
                                >
                                    Toggle Active
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <p>No users found.</p>
                )}
            </div>

            {/* Pagination */}
            <div className="pagination mt-4 flex items-center justify-between">
                <button
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
                >
                    Previous
                </button>
                <span>
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default AdminUserManagement;
